import { useMutation } from '@apollo/client';
import { useCurrentAccount } from '@application/api';
import { graphql } from '@gql/gql';
import type {
  SendMessageMutationMutation,
  SendMessageMutationMutationVariables,
} from '@gql/graphql';
import type { MtMessagePageFormValues } from '@presentation/pages';
import { useCallback } from 'react';
import { ACCOUNT_GET_MESSAGES } from './useGetMessages';

export const SEND_MESSAGE = graphql(`
  mutation SendMessageMutation($data: SendMessageInput!) {
    sendMessage(data: $data) {
      id
      content
      createdAt
      seen
      img
      file
      reported
      fromAccountId
      toAccountId
    }
  }
`);

export interface SendMessageFromFormParams extends MtMessagePageFormValues {
  otherAccountId: number;
}

export const useSendMessage = (otherAccountId = 0) => {
  const { currentAccount } = useCurrentAccount();
  const [sendMessageMutation, { error, loading }] = useMutation<
    SendMessageMutationMutation,
    SendMessageMutationMutationVariables
  >(SEND_MESSAGE, {
    awaitRefetchQueries: true,
    update(cache, { data }) {
      const cachedData = cache.readQuery({
        query: ACCOUNT_GET_MESSAGES,
        variables: { otherAccountId },
      });
      cache.writeQuery({
        query: ACCOUNT_GET_MESSAGES,
        data: {
          ...cachedData,
          messages: [...(cachedData?.messages ?? []), data?.sendMessage],
        },
        variables: {
          otherAccountId,
        },
      });
    },
  });

  const sendMessageFromForm = useCallback(
    async (params: SendMessageFromFormParams) => {
      const { otherAccountId: otherAccountIdParam, file, message } = params;

      const messageImg: Scalar_Upload | undefined = typeof file !== 'string' ? file : undefined;

      const optimisticImg = messageImg ? URL.createObjectURL(file as File) : null;

      await sendMessageMutation({
        variables: {
          data: {
            toAccountId: otherAccountIdParam,
            content: message ? message.trim() : undefined,
            image: messageImg,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          sendMessage: {
            id: 9999,
            seen: false,
            img: optimisticImg,
            file: null,
            content: message ?? null,
            reported: false,
            fromAccountId: currentAccount?.id ?? 0,
            toAccountId: otherAccountIdParam ?? otherAccountId ?? 0,
            createdAt: new Date().toISOString(),
          },
        },
      });
    },
    [currentAccount?.id, otherAccountId, sendMessageMutation],
  );

  return {
    sendMessageFromForm,
    sendMessageLoading: loading,
    sendMessageError: error,
  };
};
